<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import ShareButton from '@/components/utilities/ShareButton.vue';
import FileArrowDownRegular from '@/components/svg/FileArrowDownRegular.vue';
import { useBuildingFacadesApi } from '@/composables/useBuildingFacadesApi';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useUnitsQuoteApi } from '@/composables/useUnitsQuoteApi';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building.ts';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuotesInfo.ts';
import type { Unit } from '@/typings/units/Unit.ts';

import UnitSummaryActions from './UnitSummaryActions.vue';
import UnitSummaryDetails from './UnitSummaryDetails.vue';
import UnitSummaryUnitAndBuildingPlan from './UnitSummaryUnitAndBuildingPlan.vue';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  building: Building,
  unit: Unit;
}>();

const wizardStep = ref(5);

// Détecter la taille de l'écran pour mobile
const isMobile = ref(false);
const isTablet = ref(false);

const checkTablet = () => {
  isTablet.value = window.innerWidth <= 1024;
};

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const { getBuildingWizardUrlFromUnit } = usePurchaseLocalizedUrls();
const { getUnitQuoteById } = useUnitsQuoteApi();
const { getBuildingFacades } = useBuildingFacadesApi();
const newQuoteInfo = ref<NewQuoteInfo>();
const buildingFacades = ref<BuildingFacade[]>([]);
const isLoading = ref(true);
const selectedConfigurationId = ref<number | undefined>(undefined);
const selectedStyleId = ref<number | undefined>(undefined);
const selectedOptionIds = ref<number[] | undefined>(undefined);
const isFirstTimeHomebuyer = ref(false);

function updateSelectedOptions(options: number[]) {
  selectedOptionIds.value = options;
}

function onConfigurationChange(configId: number | undefined) {
  selectedConfigurationId.value = configId;
}

const unitName = computed(() => {
  const address = locale.value.toLowerCase() === 'fr-ca' ? props.unit.fullAddress : props.unit.fullAddressEn;
  const baseTitle = props.unit.hasDefaultOptions ? `${address} (${t('unitSummary.preConfigured').toLocaleLowerCase()})` : address;
  return `${baseTitle}`;
});

const dynamicPdfUrl = computed(() => {
  if (!newQuoteInfo.value?.unit?.id || selectedConfigurationId.value == null) {
    return null;
  }
  
  const baseUrl = '/umbraco/api/units/unit-pdf';
  const unitId = newQuoteInfo.value.unit.id;
  const configurationId = selectedConfigurationId.value;
  const currentLocale = locale.value || 'fr';
  
  return `${baseUrl}/${unitId}?selectedConfiguration=${configurationId}&locale=${currentLocale}`;
});

onMounted(async () => {
  // Initialiser la détection mobile
  checkMobile();
  checkTablet();
  window.addEventListener('resize', checkMobile);
  window.addEventListener('resize', checkTablet); 

  try {
    const optionRequest = {
      locale: locale.value.substring(0, 2),
    };
    const [quoteData, facadesData] = await Promise.all([
      getUnitQuoteById(props.unit.id, optionRequest),
      getBuildingFacades(props.building.id)
    ]);
    newQuoteInfo.value = quoteData;
    buildingFacades.value = facadesData;
    
  } catch (error) {
    console.error('Error fetching quote data:', error);
  } finally {
    isLoading.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  window.removeEventListener('resize', checkTablet);
});

function onBackToUnitSelection() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('returnUrl')) {
    const returnUrl = params.get('returnUrl');
    if (returnUrl) {
      window.location.href = returnUrl;
    }
  } else {
    window.location.href = getBuildingWizardUrlFromUnit(props.unit);
  }  
}
</script>

<template>
  <!--Achat - Étape 5 - Détails de l'unité-->
  <div class="unit-summary">
    <div class="wrapper wrapper_large">
      <LoadingSpinner v-if="isLoading" />
      
      <template v-else-if="newQuoteInfo">
        <!-- Étapes du wizard -->
        <UnitSelectionWizardSteps v-model="wizardStep" />
        <div class="main-content">
          <div class="unitSummary">
            <div class="unitSummary-header">
              <!--En-tête avec bouton de retour-->
              <HeaderWithBackButton :title="unitName"
                                    :hide-title="true"
                                    @back-click="onBackToUnitSelection" />
              <div class="header-actions">
                <div v-if="isTablet" class="pdfDownload">
                  <a v-if="dynamicPdfUrl"
                     :href="dynamicPdfUrl"
                     target="_blank">
                    <FileArrowDownRegular />
                  </a>
                </div>
                <ShareButton :unit-name="unitName" />
              </div>
            </div>
            <div v-if="isMobile" class="unitNameMobile">
              <h1>{{ unitName }}</h1>
            </div>
            <div id="pdfElement" class="unitSummary-container">
              <div class="unitSummary-plansContainer">
                <UnitSummaryUnitAndBuildingPlan :unit="newQuoteInfo.unit"
                                                :building-plan-image-url="building.buildingImageUrl"
                                                :building-facades="buildingFacades"
                                                :selected-configuration-id="selectedConfigurationId" />
              </div>
              <div class="unitSummary-detailsContainer">
                <UnitSummaryDetails :unit="newQuoteInfo.unit"
                                    :building="props.building"
                                    :unit-styles="newQuoteInfo.unitStyles"
                                    :options="newQuoteInfo.options"
                                    @update:selectedConfigurationId="onConfigurationChange"
                                    @update:selectedStyleId="selectedStyleId = $event"
                                    @update:selectedOptions="updateSelectedOptions"
                                    @update:isFirstTimeHomebuyer="isFirstTimeHomebuyer = $event" />
              </div>
              <div class="unitSummary-actionsContainer">
                <UnitSummaryActions :quote-info="newQuoteInfo"
                                    :unit="newQuoteInfo.unit"
                                    :selected-configuration-id="selectedConfigurationId"
                                    :selected-style-id="selectedStyleId"
                                    :selected-option-ids="selectedOptionIds || []"
                                    :is-first-time-homebuyer="isFirstTimeHomebuyer" />
              </div>
            </div>
          </div>
        </div>
      </template>
    
      <div v-else
           class="unitSummary-error">
        {{ t('unitSummary.errorLoadingData') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/unitSummary.scss';
</style>
